import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../store";
import { setHandoff, setHandoffExtra } from "../slices/handoffSlice";
import { updateHandoff } from "../actions/sharedActions";
import {
  X,
  Send,
  Loader2,
  CheckCircle2,
  Mail,
  XCircle,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { usePrompt } from "../hooks";
import { HandoffOffer } from "./types";
import { updateUserDataAndPing } from "../slices/userSlice";
import TextareaAutosize from "react-textarea-autosize";

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  subject?: string;
  body?: string;
  email?: string;
}

const HandoffForm = ({ handoffId }: { handoffId: string }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isShow: isPromptShow, setIsShow: setIsPromptShow } = usePrompt();
  const [isLoading, setIsLoading] = useState<
    "no" | "cancelling" | "submitting"
  >("no");
  const [error, setError] = useState<string | null>(null);

  const user = useAppSelector((state) => state.user.data);
  const handoff = useAppSelector((state) => state.handoff.data[handoffId]);
  const extraData = useAppSelector(
    (state) => state.handoff.extraData[handoffId]
  ) || {
    firstName: user.user_data.first_name || "",
    lastName: user.user_data.last_name || "",
  };

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<ValidationErrors>({});

  const isNameRequired = true;

  useEffect(() => {
    if (handoff.status === "DRAFT" && isPromptShow) {
      setIsPromptShow(false);
    }
    // NOTE(liamvdv): make sure we re-show the prompt on unmount
    return () => {
      if (!isPromptShow) setIsPromptShow(true);
    };
  }, [isPromptShow, setIsPromptShow, handoff.status]);

  // Initialize handoff data if needed
  useEffect(() => {
    if (handoff.status === "DRAFT") {
      dispatch(
        setHandoff({
          handoff: {
            ...handoff,
            subject: handoff.subject || "",
            body: handoff.body || "",
            user_email: handoff.user_email || user.user_data.email || "",
          },
        })
      );
    }
  }, []);

  const validate = (
    handoff: HandoffOffer,
    extraData: { firstName: string; lastName: string } // Add extraData parameter
  ): ValidationErrors => {
    const errors: ValidationErrors = {};

    if (isNameRequired) {
      if (!extraData.firstName?.trim()) {
        errors.firstName = t("handoff.validation.firstName_required");
      }
      if (!extraData.lastName?.trim()) {
        errors.lastName = t("handoff.validation.lastName_required");
      }
    }

    if (!handoff.subject?.trim()) {
      errors.subject = t("handoff.validation.subject_required");
    }

    if (!handoff.body?.trim()) {
      errors.body = t("handoff.validation.body_required");
    } else if (handoff.body.trim().length < 10) {
      errors.body = t("handoff.validation.body_too_short");
    }

    if (!handoff.user_email) {
      errors.email = t("handoff.validation.email_required");
    } else if (
      !/^[a-zA-Z0-9._+-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(
        handoff.user_email
      )
    ) {
      errors.email = t("handoff.validation.email_invalid");
    }

    return errors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (["firstName", "lastName"].includes(name)) {
      // Update extra data
      dispatch(
        setHandoffExtra({
          handoffId,
          ...extraData,
          [name]: value,
        })
      );
    } else {
      // Update handoff data
      dispatch(
        setHandoff({
          handoff: {
            ...handoff,
            [name === "email" ? "user_email" : name]: value,
          },
        })
      );
    }
    // Clear error when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate field on blur
    const fieldErrors = validate(handoff, extraData);
    if (fieldErrors[name as keyof ValidationErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: fieldErrors[name as keyof ValidationErrors],
      }));
    }
  };

  const validateAll = (): boolean => {
    const validationErrors = validate(handoff, extraData);
    setErrors(validationErrors);

    const touchedFields: Record<string, boolean> = {};
    ["firstName", "lastName", "subject", "body", "email"].forEach((key) => {
      touchedFields[key] = true;
    });
    setTouched(touchedFields);

    return Object.keys(validationErrors).length > 0;
  };
  const handleAction = async (status: "SUBMITTED" | "CANCELLED") => {
    if (status === "SUBMITTED" && validateAll()) return;

    setIsLoading(status === "SUBMITTED" ? "submitting" : "cancelling");
    setError(null);

    try {
      // Update handoff
      const updatedHandoff = {
        ...handoff,
        status,
        user_email: status === "CANCELLED" ? null : handoff.user_email,
      };

      // For submit, update user data first
      if (status === "SUBMITTED") {
        dispatch(
          updateUserDataAndPing({
            first_name: user.user_data.first_name ?? extraData.firstName,
            last_name: user.user_data.last_name ?? extraData.lastName,
            email: user.user_data.email ?? handoff.user_email ?? undefined,
          })
        );
      }

      // Update handoff in API
      await dispatch(updateHandoff({ handoff: updatedHandoff })).unwrap();

      // Update local state to match
      dispatch(setHandoff({ handoff: updatedHandoff }));
      setIsPromptShow(true);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsPromptShow(true);
    } finally {
      setIsLoading("no");
    }
  };

  const handleSubmit = () => handleAction("SUBMITTED");
  const handleCancel = () => handleAction("CANCELLED");

  const getInputClassName = (fieldName: keyof ValidationErrors) => {
    const baseClasses =
      "w-full px-3 py-1.5 border rounded-md shadow-sm focus:outline-none focus:ring-1 min-h-[38px] grid grid-cols-1 ";
    if (!touched[fieldName]) {
      return (
        baseClasses + "border-gray-300 focus:ring-brand focus:border-brand"
      );
    }
    if (errors[fieldName]) {
      return (
        baseClasses + "border-red-500 focus:ring-red-300 focus:border-red-300"
      );
    }
    return baseClasses + "border-gray-300 focus:ring-brand focus:border-brand";
  };

  return (
    <div className="space-y-2 px-0.5 py-0.5">
      <div className="space-y-2">
        <label
          htmlFor="subject"
          className="block text-sm font-medium text-gray-700"
        >
          {t("handoff.form.issue_label")}
        </label>
        <input
          id="subject"
          name="subject"
          value={handoff.subject}
          onChange={handleChange}
          onBlur={handleBlur}
          className={getInputClassName("subject")}
        />
        {touched.subject && errors.subject && (
          <p className="mt-1 text-sm text-red-500">{errors.subject}</p>
        )}
      </div>

      <div className="space-y-2">
        <label
          htmlFor="body"
          className="block text-sm font-medium text-gray-700"
        >
          {t("handoff.form.details_label")}
        </label>
        {/* @ts-ignore */}
        <TextareaAutosize
          id="body"
          name="body"
          value={handoff.body}
          onChange={handleChange as any}
          onBlur={handleBlur}
          // NOTE(liamvdv): overflow hidden is needed explicitly to ensure that 'ScrollLock' will NOT catch the touch event
          className={getInputClassName("body") + " overflow-hidden"}
          rows={3}
        />
        {touched.body && errors.body && (
          <p className="mt-1 text-sm text-red-500">{errors.body}</p>
        )}
      </div>

      {isNameRequired && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700"
            >
              {t("handoff.form.firstName_label")}
            </label>
            <input
              required={isNameRequired}
              id="firstName"
              name="firstName"
              value={extraData.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("handoff.form.firstName_placeholder")}
              className={getInputClassName("firstName")}
              autoComplete="given-name"
            />
            {touched.firstName && errors.firstName && (
              <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700"
            >
              {t("handoff.form.lastName_label")}
            </label>
            <input
              required={isNameRequired}
              id="lastName"
              name="lastName"
              value={extraData.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("handoff.form.lastName_placeholder")}
              className={getInputClassName("lastName")}
              autoComplete="family-name"
            />
            {touched.lastName && errors.lastName && (
              <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <label
          htmlFor="email"
          className="block text-sm font-medium text-gray-700"
        >
          {t("handoff.form.email_label")}
        </label>
        <input
          required
          id="email"
          type="email"
          name="email"
          value={handoff.user_email || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={t("handoff.form.email_placeholder")}
          className={getInputClassName("email")}
          autoComplete="email"
        />
        {touched.email && errors.email && (
          <p className="mt-1 text-sm text-red-500">{errors.email}</p>
        )}
      </div>

      <span className="text-chat-text-light text-sm">
        {t("handoff.form.email_notice")}
      </span>

      {error && (
        <ErrorMessage title={t("handoff.error.title")} message={error} />
      )}

      <div className="flex justify-end gap-3">
        <button
          onClick={handleCancel}
          disabled={isLoading !== "no"}
          className="flex w-1/2 items-center justify-center gap-2 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand disabled:opacity-50"
        >
          {isLoading === "cancelling" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <X className="h-4 w-4" />
              {t("handoff.form.cancel_button")}
            </>
          )}
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading !== "no"}
          className="flex w-1/2 items-center justify-center gap-2 py-3 text-sm font-medium text-white bg-brand border border-transparent rounded-md hover:bg-brand-hover focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand disabled:opacity-50"
        >
          {isLoading === "submitting" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("handoff.form.sending_button")}
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              {t("handoff.form.send_button")}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// NOTE(liamvdv): we need to use redux for this because offer can update any time.
export const HandoffMessageItem = ({ offer }: { offer: HandoffOffer }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const handoffId = offer.id;
  const handoff = useAppSelector((state) => state.handoff.data[handoffId]);
  const status =
    useAppSelector((state) => state.handoff.status[handoffId]) ?? "idle";

  useEffect(() => {
    if (status === "idle") dispatch(setHandoff({ handoff: offer }));
  });

  if (status === "idle") return <></>;
  switch (handoff.status) {
    case "DRAFT":
      return <HandoffForm handoffId={handoff.id} />;
    case "SUBMITTED":
      return (
        <div className="space-y-4">
          <div
            className="w-full flex items-center justify-between px-4 py-3 
                           rounded-lg border border-gray-100 bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-brand" />
              <span className="font-medium">{t("handoff.success")}</span>
            </div>

            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="text-sm inline">{t("handoff.check_email")}</span>
            </div>
          </div>

          <p>{t("handoff.anything_else")}</p>
        </div>
      );
    case "CANCELLED":
      return (
        <button
          onClick={() =>
            dispatch(setHandoff({ handoff: { ...handoff, status: "DRAFT" } }))
          }
          className="w-full group flex items-center justify-between px-4 py-3 
                   rounded-lg border border-gray-100 hover:border-gray-200
                   hover:bg-gray-50 active:bg-gray-100 transition-all
                   focus:outline-none"
        >
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5 text-brand" />
            <span className="font-medium text-chat-text">
              {t("handoff.rejected")}
            </span>
          </div>

          <div className="flex items-center gap-2 text-gray-400 group-hover:text-brand">
            <span className="text-sm hidden sm:inline">
              {t("handoff.edit")}
            </span>
            <ArrowRight className="h-4 w-4" />
          </div>
        </button>
      );
    default:
      throw new Error(`Unknown handoff status ${handoff.status}`);
  }
};

const ErrorMessage = ({
  title = "Validation Error",
  message,
}: {
  title: string;
  message: string;
}) => {
  return (
    <div className="not-prose">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-medium text-red-800">{title}</h3>
          <p className="mt-1 text-sm text-red-700">{message}</p>
        </div>
      </div>
    </div>
  );
};

const useCombinedRefs = <T,>(
  ...refs: (React.ForwardedRef<T> | React.RefObject<T>)[]
) => {
  const targetRef = useRef<T>(null);

  useEffect(() => {
    refs.forEach((ref) => {
      if (!ref) return;

      if (typeof ref === "function") {
        ref(targetRef.current);
      } else {
        (ref as React.MutableRefObject<T | null>).current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef;
};
